<template>
  <div class="contact-page">
    <!-- 添加导航触发区域 -->
    <ProductNavigation />

    <!-- 关于我们页面头部 -->
    <section class="contact-hero-section">
      
    </section>

    <!-- 公司介绍部分 -->
    <section class="company-intro-section">
        <div class="section-content">
            <div class="intro-container">
                <div class="intro-text">
                    <h2>{{ locale === 'en' ? 'Why choose Fabet?' : '为啥选择法彼特' }}</h2>
                    <div v-if="loading" class="loading">{{ locale === 'en' ? 'Loading...' : '加载中...' }}</div>
                    <div v-else class="about-us-content">
                        <div class="about-us-content">
                          <div class="intro-container">
                            <p class="intro-paragraph">{{ locale === 'en' ? (aboutUsContent.introEn || aboutUsContent.intro) : aboutUsContent.intro }}</p>
                          </div>
                        </div>

                          <div class="logo-section">
                            <div class="logo-image">
                              <img src="/assets/fabet_logo.png" alt="FABET Logo">
                            </div>
                            <p class="logo-title">{{ locale === 'en' ? 'Meaning of the Logo' : 'LOGO寓意' }}</p>
                            <p
                              class="logo-explain" style="text-indent: 1em;"
                              v-html="locale === 'en' ? (aboutUsContent.logoExplainEn || aboutUsContent.logo_explain) : aboutUsContent.logo_explain"
                            ></p>
                          </div>
                          
                          <div class="values-section">
                            <h3>{{ locale === 'en' ? 'Values:' : '价值观：' }}</h3>
                            <p>{{ locale === 'en' ? (aboutUsContent.valuesEn || aboutUsContent.values) : aboutUsContent.values }}</p>
                          </div>
                    </div>
                </div>
                <div class="intro-image">
                    <img class="company-image" :src="companyBuildingImageUrl" alt="公司大楼">
                </div>
            </div>
        </div>
    </section>

    <!-- 联系我们内容区域 -->
    <section class="contact-content-section">
        <div class="contact-container">
           <!-- 联系表单触发器图片 -->
           <div class="contact-form-trigger" @click="showContactForm = !showContactForm" @mouseenter="showContactForm = true">
            <img src="/assets/con_img.png" :alt="t('contact.showFormHint')" class="contact-trigger-image">
            <div class="contact-trigger-overlay">
              <span class="contact-trigger-text">{{ t('contact.contactUs') }}</span>
            </div>
          </div>
            
           <!-- 联系表单 - 使用条件渲染 -->
          <transition name="slide-down">
            <div class="contact-form" v-show="showContactForm" transition="fade">
              <h3>{{ t('contact.messageFeedback') }}</h3>
              <form @submit.prevent="handleSubmit">
                <div class="form-group">
                  <label for="name">{{ t('contact.name') }}</label>
                  <input type="text" id="name" v-model="formData.name" :placeholder="t('contact.namePlaceholder')" required>
                </div>
                <div class="form-group">
                  <label for="email">{{ t('contact.email') }}</label>
                  <input type="email" id="email" v-model="formData.email" :placeholder="t('contact.emailPlaceholder')" required>
                </div>
                <div class="form-group">
                  <label for="phone">{{ t('contact.phone') }}</label>
                  <input type="tel" id="phone" v-model="formData.phone" :placeholder="t('contact.phonePlaceholder')">
                </div>
                <div class="form-group">
                  <label for="message">{{ t('contact.message') }}</label>
                  <textarea id="message" v-model="formData.message" rows="5" :placeholder="t('contact.messagePlaceholder')" required></textarea>
                </div>
                <button type="submit" class="submit-btn" :disabled="submissionStatus === 'submitting'">
                  {{ submissionStatus === 'submitting' ? t('contact.submitting') : t('contact.submit') }}
                </button>
                <div v-if="submissionStatus === 'success'" class="success-message">{{ t('contact.submitSuccess') }}</div>
                <div v-if="submissionStatus === 'error'" class="error-message">{{ t('contact.submitError') }}</div>
              </form>
            </div>
          </transition>

          <!-- 联系信息 -->
          
      </div>
    </section>

    <!-- 页脚内容 -->
    <Footer />
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import ProductNavigation from '@/components/ProductNavigation.vue';
import Footer from '@/components/Footer.vue';
import { getContactAboutImages } from '@/api/contactAbout';
import { submitMessage } from '@/api/message';
import { getAboutUsContent } from '@/api/contactAbout';

const { t, locale } = useI18n();

// 表单数据
const formData = ref({
  name: '',
  email: '',
  phone: '',
  message: ''
});

// 控制表单显示/隐藏的状态变量
const showContactForm = ref(false);

// 添加一个点击外部区域关闭表单的功能
onMounted(() => {
  document.addEventListener('click', handleClickOutside);
});

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside);
});

function handleClickOutside(event) {
  const contactForm = document.querySelector('.contact-form');
  const contactTrigger = document.querySelector('.contact-form-trigger');
  
  // 如果点击的不是表单或触发器，且表单是显示状态，则关闭表单
  if (
    contactForm && 
    contactTrigger && 
    showContactForm.value &&
    !contactForm.contains(event.target) &&
    !contactTrigger.contains(event.target)
  ) {
    showContactForm.value = false;
  }
}

// 图片URL状态
const companyBuildingImageUrl = ref('/assets/company.jpg');
const submissionStatus = ref('');  // 提交状态

// 关于我们内容状态
const aboutUsContent = ref({
  intro: '',
  logo_explain: '',
  values: '',
  color_meanings: []
});

const loading = ref(false);

// 加载关于我们内容
const loadAboutUsContent = async () => {
  loading.value = true;
  try {
    const response = await getAboutUsContent(locale.value);
    // 修改为访问 response.data.data
    const data = response.data?.data || {};
    aboutUsContent.value = {
      intro: data.intro || '',
      introEn: data.introEn || '',
      logo_explain: data.logo_explain || '',
      logoExplainEn: data.logoExplainEn || '',
      values: data.values || '',
      valuesEn: data.valuesEn || '',
      color_meanings: data.color_meanings || []
    };
  } catch (error) {
    console.error('加载关于我们内容失败:', error);
    aboutUsContent.value = {
      intro: '',
      introEn: '',
      logo_explain: '',
      logoExplainEn: '',
      values: '',
      valuesEn: '',
      color_meanings: []
    };
  } finally {
    loading.value = false;
  }
};

// 监听语言变化，重新加载内容
watch(locale, () => {
  loadAboutUsContent();
});

// 加载图片数据
onMounted(async () => {
  // 加载关于我们内容
  await loadAboutUsContent();
  
  // 加载公司大楼图片
  try {
    const response = await getContactAboutImages();
    if (response.data.companyBuildingImageUrl) {
      companyBuildingImageUrl.value = response.data.companyBuildingImageUrl;
    }
  } catch (error) {
    console.error('加载图片数据失败:', error);
  }
});

// 提交表单
const handleSubmit = async () => {
  try {
    submissionStatus.value = 'submitting';
    const response = await submitMessage(formData.value);
    if (response.success) {
      submissionStatus.value = 'success';
      // 重置表单
      formData.value = {
        name: '',
        email: '',
        phone: '',
        message: ''
      };
      setTimeout(() => {
        submissionStatus.value = '';
      }, 3000);
    }
  } catch (error) {
    console.error('提交留言失败:', error);
    submissionStatus.value = 'error';
    setTimeout(() => {
      submissionStatus.value = '';
    }, 3000);
  }
};
</script>

<style scoped lang="scss">
@import "@/styles/sections/homepage/footer_section"; // 引入全局样式
@import '@/styles/sections/product_page/product_head';

.red-text{
  color: red;
  font-weight: bold;
}

.black-text{
  color: black;
  font-weight: normal;
}

.logo-title{
  font-size: 20px;
  font-weight: bold
 }

.contact-page {
  padding: 0;
}

.contact-hero-section {
  position: relative; /* 确保作为定位容器 */
  height: 90vh;
  background-color: #f5f5f5;
  background-image: url('@/assets/about_banner.png'); /* 背景图片路径 */
  background-size: cover; /* 覆盖整个区域 */
  background-position: center; /* 居中显示 */
  background-repeat: no-repeat; /* 不重复 */
  display: flex;
  align-items: center;
  justify-content: center;
}

.hero-content {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
}

.contact-content-section {
  padding: 60px 0;
  background-color: #d3e4fc81;
}

.section-content {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
}

// 使公司简介内容左对齐
.intro-text {
  text-align: left;
}

.about-us-content {
  text-align: left;
}

.values-section {
  text-align: left;
}

.color-meanings-section {
  text-align: left;
}

.contact-container {
  display: flex;
  flex-direction: column; /* 改为垂直布局 */
  align-items: center;
  gap: 0; /* 移除表单与触发器之间的间隙 */
  max-width: 800px;
  margin: 0 auto;
}

/* 联系表单触发器样式 */
.contact-form-trigger {
  width: 1200px;
  max-width: 1400px;
  position: relative;
  cursor: pointer;
  transition: transform 0.3s ease;
}

.contact-form-trigger:hover {
  transform: scale(1.02);
}

.contact-trigger-image {
  width: 1200px;
  height: 300px;
  border-radius: 8px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
}

.contact-trigger-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.3s ease;
}

.contact-form-trigger:hover .contact-trigger-overlay {
  background-color: rgba(0, 0, 0, 0.6);
}

.contact-trigger-text {
  color: white;
  font-size: 60px;
  font-weight: bold;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
}

/* 表单动画效果 */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* 表单下拉动画效果 */
.slide-down-enter-active,
.slide-down-leave-active {
  transition: all 0.3s ease-out; /* 加快过渡速度 */
  overflow: hidden; /* 隐藏溢出内容 */
}

.slide-down-enter-from,
.slide-down-leave-to {
  opacity: 0; /* 透明 */
  transform: translateY(-20px); /* 向上移动20px */
  max-height: 0; /* 最大高度为0 */
}

.slide-down-enter-to,
.slide-down-leave-from {
  opacity: 1; /* 不透明 */
  transform: translateY(0); /* 位置不变 */
  max-height: 800px; /* 设置足够大的最大高度 */
}


.contact-form {
  width: 100%;
  margin-right: 0;
  padding: 30px;
  background-color: #f9f9f9;
  border-radius: 0 0 8px 8px; /* 只保留底部圆角 */
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  position: relative;
  z-index: 10;
}

.contact-form h3 {
  font-size: 20px;
  color: #333;
  margin-bottom: 20px;
  padding-bottom: 10px;
  border-bottom: 1px solid #eee;
}

.form-group {
  margin-bottom: 20px;
}

.form-group label {
  display: block;
  margin-bottom: 8px;
  font-weight: bold;
  color: #333;
}

.form-group input,
.form-group textarea {
  width: 100%;
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
}

.form-group textarea {
  resize: vertical;
}

.submit-btn {
  display: block;
  width: 100%;
  padding: 12px;
  background-color: #2d7fba;
  color: #fff;
  border: none;
  border-radius: 4px;
  font-size: 16px;
  cursor: pointer;
  transition: background-color 0.3s;
}

.submit-btn:hover {
  background-color: #4287b9;
}

.contact-info {
  width: 100%; /* 宽度设为100%以适应容器 */
  margin-left: 0; /* 移除左外边距 */
  padding: 30px;
  text-align: center;
  /* 移除卡片样式 */
  /* background-color: #f9f9f9;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1); */
}

/* 新增flex容器样式，实现左右分布 */
.contact-content-wrapper {
  display: flex;
  justify-content: space-between;
  gap: 150px;  // 将间距从40px增加到150px
  align-items: flex-start;
}

/* 联系信息部分样式 */
.contact-methods-section {
  flex: 1;
  min-width: 300px;
}

/* 二维码部分样式 */
.qrcode-section {
  flex: 1;
  min-width: 300px;
}

.qrcode-container {
  margin-top: 0; /* 移除顶部外边距 */
  padding-top: 0; /* 移除顶部内边距 */
  border-top: none; /* 移除顶部边框 */
}

.qrcode-container h4 {
  font-size: 16px;
  color: #333;
  margin-bottom: 15px;
}

.qrcode-images {
  display: flex;
  gap: 40px;  // 将间距从40px增加到80px
  justify-content: center;
  margin-top: 20px;
}

.qrcode-item {
  text-align: center;
  // background-color: white;
  padding: 15px;
  border-radius: 8px;
  // box-shadow: 0 2px 5px rgba(0, 0, 0, 0.05);
}

.qrcode-item img {
  width: 120px;
  height: 120px;
  object-fit: contain;
  margin-bottom: 10px;
  border: 1px solid #eee;
  padding: 5px;
  border-radius: 4px;
}

.qrcode-item p {
  font-size: 14px;
  color: #666;
}

/* 色彩寓意小圆点样式 */
.color-meanings-section ul {
  padding-left: 20px;
  list-style: none;
}

.color-meanings-section li {
  margin-bottom: 10px;
  display: flex;
  align-items: flex-start; /* 修改为flex-start实现顶部对齐 */
  gap: 10px;
}

.color-dot {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: 1px solid #ccc;
  flex-shrink: 0;
}

.color-desc {
  color: #666;
  line-height: 1.6;
}

/* 移出媒体查询 - 全局生效的logo尺寸限制 */
.logo-section {
  text-align: left;
  margin: 20px 0;
}

.logo-image {
  display: inline-block !important;
  margin: 10px 0 !important;
  max-width: 200px !important;
  width: 200px !important;
  overflow: hidden;
}

.logo-image img {
  max-width: 100% !important;
  width: 100% !important;
  max-height: 60px !important;
  height: auto !important;
  object-fit: contain !important;
}

// 在section-content样式后面添加
.intro-container {
  display: flex;
  justify-content: space-between;
  align-items: stretch; // 修改为stretch，使子元素高度相同
  gap: 40px;
}

.intro-text {
  flex: 1;
  text-align: left;
}

.intro-image {
  flex: 1;
  display: flex;
  align-items: center; // 垂直居中
  justify-content: center; // 水平居中
  min-height: 100%; // 确保容器高度足够
}

.company-image {
  max-width: 100%;
  height: auto;
  border-radius: 8px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
}

.contact-trigger-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.3s ease;
}

.contact-form-trigger:hover .contact-trigger-overlay {
  background-color: rgba(0, 0, 0, 0.6);
}

/* 表单动画效果 */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.contact-form {
  width: 100%; /* 宽度设为100%以适应容器 */
  margin-right: 0; /* 移除右外边距 */
  padding: 30px;
  background-color: #f9f9f9;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
}

.contact-form h3 {
  font-size: 20px;
  color: #333;
  margin-bottom: 20px;
  padding-bottom: 10px;
  border-bottom: 1px solid #eee;
}

.form-group {
  margin-bottom: 20px;
}

.form-group label {
  display: block;
  margin-bottom: 8px;
  font-weight: bold;
  color: #333;
}

.form-group input,
.form-group textarea {
  width: 100%;
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
}

.form-group textarea {
  resize: vertical;
}

.submit-btn {
  display: block;
  width: 100%;
  padding: 12px;
  background-color: #42b983;
  color: #fff;
  border: none;
  border-radius: 4px;
  font-size: 16px;
  cursor: pointer;
  transition: background-color 0.3s;
}

.submit-btn:hover {
  background-color: #389e72;
}

.contact-info {
  width: 100%; /* 宽度设为100%以适应容器 */
  margin-left: 0; /* 移除左外边距 */
  padding: 30px;
  text-align: center;
  /* 移除卡片样式 */
  /* background-color: #f9f9f9;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1); */
}

/* 新增flex容器样式，实现左右分布 */
.contact-content-wrapper {
  display: flex;
  justify-content: space-between;
  gap: 150px;  // 将间距从40px增加到150px
  align-items: flex-start;
}

/* 联系信息部分样式 */
.contact-methods-section {
  flex: 1;
  min-width: 300px;
}

/* 二维码部分样式 */
.qrcode-section {
  flex: 1;
  min-width: 300px;
}

.qrcode-container {
  margin-top: 0; /* 移除顶部外边距 */
  padding-top: 0; /* 移除顶部内边距 */
  border-top: none; /* 移除顶部边框 */
}

.qrcode-container h4 {
  font-size: 16px;
  color: #333;
  margin-bottom: 15px;
}

.qrcode-images {
  display: flex;
  gap: 40px;  // 将间距从40px增加到80px
  justify-content: center;
  margin-top: 20px;
}

.qrcode-item {
  text-align: center;
  // background-color: white;
  padding: 15px;
  border-radius: 8px;
  // box-shadow: 0 2px 5px rgba(0, 0, 0, 0.05);
}

.qrcode-item img {
  width: 120px;
  height: 120px;
  object-fit: contain;
  margin-bottom: 10px;
  border: 1px solid #eee;
  padding: 5px;
  border-radius: 4px;
}

.qrcode-item p {
  font-size: 14px;
  color: #666;
}

/* 色彩寓意小圆点样式 */
.color-meanings-section ul {
  padding-left: 20px;
  list-style: none;
}

.color-meanings-section li {
  margin-bottom: 10px;
  display: flex;
  align-items: flex-start; /* 修改为flex-start实现顶部对齐 */
  gap: 10px;
}

.color-dot {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: 1px solid #ccc;
  flex-shrink: 0;
}

.color-desc {
  color: #666;
  line-height: 1.6;
}

/* 移出媒体查询 - 全局生效的logo尺寸限制 */
.logo-section {
  text-align: left;
  margin: 20px 0;
}

.logo-image {
  display: inline-block !important;
  margin: 10px 0 !important;
  max-width: 200px !important;
  width: 200px !important;
  overflow: hidden;
}

.logo-image img {
  max-width: 100% !important;
  width: 100% !important;
  max-height: 60px !important;
  height: auto !important;
  object-fit: contain !important;
}

// 响应式设计
@media (max-width: 768px) {
  .company-intro-section {
    padding: 40px 0;
  }
  
  .intro-container {
    flex-direction: column;
  }
  
  /* 这里可以保留或修改移动设备上的logo样式 */
  .company-image {
    margin-left: 0;
  }
}

// 响应式设计 - 手机端适配
@media (max-width: 768px) {
  /* 整体页面调整 */
  .contact-page {
    overflow-x: hidden;
  }
  
  /* 英雄区调整 */
  .contact-hero-section {
    height: 60vh;
    min-height: 400px;
    background-position: center center;
    background-size: cover;
  }

  .hero-content {
    padding: 0 15px;
  }
  
  .contact-trigger-text {
    font-size: 36px;
    text-align: center;
    padding: 0 20px;
  }
  
  /* 联系表单触发器调整 */
  .contact-form-trigger {
    width: 100%;
    max-width: 100%;
    margin: 0 10px;
  }
  
  .contact-trigger-image {
    width: 100%;
    height: 200px;
  }
  
  /* 内容区域调整 */
  .contact-content-section {
    padding: 40px 0;
  }
  
  .section-content {
    padding: 0 15px;
  }
  
  /* 简介容器调整 */
  .intro-container {
    flex-direction: column;
    gap: 20px;
  }
  
  /* 联系容器调整 */
  .contact-container {
    gap: 20px;
    max-width: 100%;
    padding: 0 15px;
  }
  
  /* 表单调整 */
  .contact-form {
    padding: 20px;
  }
  
  .contact-form h3 {
    font-size: 18px;
  }
  
  .form-group input,
  .form-group textarea {
    padding: 8px;
    font-size: 13px;
  }
  
  /* 联系信息调整 */
  .contact-info {
    padding: 20px 15px;
  }
  
  /* 联系内容容器调整 */
  .contact-content-wrapper {
    flex-direction: column;
    gap: 30px;
  }
  
  .contact-methods-section,
  .qrcode-section {
    min-width: auto;
  }
  
  /* 二维码部分调整 */
  .qrcode-images {
    flex-direction: column;
    gap: 20px;
  }
  
  .qrcode-item {
    padding: 10px;
  }
  
  .qrcode-item img {
    width: 100px;
    height: 100px;
  }
  
  /* 公司简介部分调整 */
  .company-intro-section {
    padding: 40px 0;
  }
  
  .company-image {
    margin-left: 0;
    border-radius: 6px;
  }
  
  /* 色彩寓意小圆点调整 */
  .color-dot {
    width: 16px;
    height: 16px;
  }
  
  .color-meanings-section li {
    gap: 8px;
  }
  
  /* Logo调整 */
  .logo-section {
    text-align: center;
  }
  
  .logo-image {
    max-width: 180px !important;
    width: 180px !important;
    margin: 10px auto !important;
  }
}

/* 小屏手机优化 (max-width: 480px) */
@media (max-width: 480px) {
  /* 英雄区进一步调整 */
  .contact-hero-section {
    height: 50vh;
    min-height: 350px;
  }
  
  .contact-trigger-text {
    font-size: 28px;
    line-height: 1.2;
  }
  
  .contact-trigger-image {
    height: 150px;
  }
  
  /* 内容区域进一步调整 */
  .contact-content-section {
    padding: 30px 0;
  }
  
  /* 表单进一步调整 */
  .contact-form {
    padding: 15px;
  }
  
  .contact-form h3 {
    font-size: 16px;
  }
  
  /* 按钮调整 */
  .submit-btn {
    padding: 10px;
    font-size: 14px;
  }
  
  /* 文本大小调整 */
  p,
  .color-desc,
  .qrcode-item p {
    font-size: 13px;
  }
  
  h2 {
    font-size: 20px;
  }
  
  h3 {
    font-size: 16px;
  }
  
  /* Logo进一步调整 */
  .logo-image {
    max-width: 150px !important;
    width: 150px !important;
  }
}

/* 480px以下屏幕的banner适配 */
@media (max-width: 480px) {
  .contact-hero-section {
    height: 50vh;
    min-height: 350px;
    background-position: center center;
  }

  .hero-content {
    padding: 0 10px;
  }
}

/* 360px以下屏幕的banner适配 */
@media (max-width: 360px) {
  .contact-hero-section {
    height: 45vh;
    min-height: 300px;
  }
}

</style>